/**
 * SOVRANOVA Offline Write Outbox
 * ------------------------------------------------------------
 * The write half of offline continuity. When the user creates
 * something offline (a journal entry, a mood/outcome score, a
 * session message) and the backend is unreachable or mid-upgrade,
 * the write is queued here in IndexedDB instead of being lost.
 * On reconnect it is replayed to the backend in order, then cleared.
 *
 * This is the generalised "catch it offline, re-upload when the
 * service returns" guarantee — nothing the user does is ever lost.
 *
 * Design rules:
 *  - Only allowlisted POST paths are ever queued (QUEUEABLE below).
 *    Token fetches, greeting/TTS generation, and anything whose
 *    response the UI needs immediately are NEVER queued.
 *  - IndexedDB (not localStorage) so payloads aren't size-bound and
 *    survive far better across upgrades.
 *  - Per-user stamped; replay is ordered (FIFO) and idempotent-safe.
 *  - Nothing here ever throws into the app — queueing must never break UX.
 */

import { encryptToString, decryptFromString } from './deviceCrypto';

const DB_NAME = 'sovranova-outbox';
const STORE   = 'pending';
const DB_VERSION = 1;

// POST paths whose writes are safe to queue offline and replay on reconnect.
// Deliberately conservative — only durable user-content writes.
const QUEUEABLE = [
  '/journal/entry',
  '/checkin',
  '/outcomes/phq9',
  '/outcomes/gad7',
  '/anchor/episode',
  '/session/message',
];

export function isQueueablePath(path) {
  if (!path) return false;
  const clean = path.split('?')[0];
  return QUEUEABLE.some(p => clean === p || clean.startsWith(p));
}

function openDB() {
  return new Promise((resolve, reject) => {
    try {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE)) {
          db.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true });
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror   = () => reject(req.error);
    } catch (e) { reject(e); }
  });
}

// Queue a failed write. Returns true if successfully queued.
export async function enqueueWrite(userId, path, body) {
  try {
    // Encrypt the durable write at rest. If encryption is unavailable, fall back
    // to plaintext rather than LOSING the user's content — the "nothing lost"
    // guarantee outranks at-rest encryption for durable writes (cache differs).
    const encBody = await encryptToString(body);
    const db = await openDB();
    return await new Promise((resolve) => {
      try {
        const record = {
          userId: userId || 'anon',
          path,
          queuedAt: Date.now(),
          ...(encBody !== null ? { encBody } : { body: body || null }),
        };
        const tx = db.transaction(STORE, 'readwrite');
        tx.objectStore(STORE).add(record);
        tx.oncomplete = () => { db.close(); resolve(true); };
        tx.onerror    = () => { db.close(); resolve(false); };
      } catch (_) { try { db.close(); } catch (_) {} resolve(false); }
    });
  } catch (_) { return false; }
}

// Read all pending writes for a user, oldest first.
function allPending(userId) {
  return openDB().then(db => new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).getAll();
      req.onsuccess = () => {
        db.close();
        const uid = userId || 'anon';
        resolve((req.result || [])
          .filter(r => r.userId === uid)
          .sort((a, b) => a.queuedAt - b.queuedAt));
      };
      req.onerror = () => { db.close(); resolve([]); };
    } catch (_) { try { db.close(); } catch (_) {} resolve([]); }
  })).catch(() => []);
}

function removeEntry(id) {
  return openDB().then(db => new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).delete(id);
      tx.oncomplete = () => { db.close(); resolve(true); };
      tx.onerror    = () => { db.close(); resolve(false); };
    } catch (_) { try { db.close(); } catch (_) {} resolve(false); }
  })).catch(() => false);
}

export function pendingCount(userId) {
  return allPending(userId).then(rows => rows.length);
}

// Replay all pending writes for a user, oldest first, using the provided
// sender (api's raw POST). Stops at the first failure so order is preserved
// and nothing is lost. Returns { sent, remaining }.
//
// `sender(path, body)` must resolve on success and reject on failure.
export async function flushOutbox(userId, sender) {
  let sent = 0;
  try {
    const rows = await allPending(userId);
    for (const row of rows) {
      // Decrypt the body if stored encrypted. A null result means the device key
      // is gone (e.g. storage cleared) and the row is unrecoverable — drop it so
      // it can't block the queue forever.
      let body;
      if (row.encBody != null) {
        body = await decryptFromString(row.encBody);
        if (body === null) { await removeEntry(row.id); continue; }
      } else {
        body = row.body;
      }
      try {
        await sender(row.path, body);
        await removeEntry(row.id);
        sent++;
      } catch (_) {
        // Still failing (offline / server down). Leave this and the rest
        // queued, in order, for the next reconnect.
        break;
      }
    }
    const remaining = await pendingCount(userId);
    return { sent, remaining };
  } catch (_) {
    return { sent, remaining: -1 };
  }
}

// Clear ALL queued writes for every user (called on sign-out, like the cache).
export function clearOutbox() {
  return openDB().then(db => new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).clear();
      tx.oncomplete = () => { db.close(); resolve(true); };
      tx.onerror    = () => { db.close(); resolve(false); };
    } catch (_) { try { db.close(); } catch (_) {} resolve(false); }
  })).catch(() => false);
}
